<template>
  <div
    class="c-contributors"
    :class="{
      'c-card__contributors c-contributors--small': theme === 'card',
      'c-banner__contributors': theme === 'banner'
    }"
  >
    <div
      v-if="isLoading"
      class="c-loading__container"
    >
      <SVGIcon class="loading-icon" width="32" height="32" name="loading" />
    </div>
    <template v-else>
      <!-- show a placeholder during loading / errors to avoid DOM reflow -->
      <span
        v-if="!(project && project.owner) && (getContributors.loading || getContributors.err)"
        class="c-contributors__contributor"
      >
        …
      </span>
      <a
        v-if="project && project.owner"
        :href="`/profile/${project.owner.username}/`"
        class="c-contributors__contributor c-contributors__owner"
        :title="`${project.owner.username} is the owner of this project`"
      >
        <div class="c-contributors__picture-container">
          <template v-if="project.owner.forum_user.avatar_url">
            <SVGIcon class="c-contributors__picture-border" width="32" height="32" name="avatar-outline-thick" />
            <img
              :src="project.owner.forum_user.avatar_url"
              :alt="project.owner.username.charAt(0).toUpperCase()"
              class="c-contributors__picture"
            >
          </template>
          <template v-else>
            <SVGIcon class="c-contributors__avatar-placeholder" name="avatar" />
          </template>
        </div>
      </a>
      <template v-if="project && project.owner">
        <SVGIcon v-if="project.owner.forum_user.is_premium" class="c-contributors__premium-icon" name="diamond-small" />
      </template>
      <template v-if="!getContributors.loading && !getContributors.err">
        <a
          v-for="contributor of getContributors.data"
          :key="contributor.oauth_id"
          :href="contributor.profile_url"
          class="c-contributors__contributor"
          :title="contributor.display_name"
        >
          <template v-if="contributor.avatar_url">
            <div class="c-contributors__picture-container">
              <SVGIcon class="c-contributors__picture-border" width="32" height="32" name="avatar-outline-thick" />
              <img
                :src="contributor.avatar_url"
                :alt="contributor.display_name.charAt(0).toUpperCase()"
                class="c-contributors__picture"
              >
            </div>
          </template>
          <template v-else>
            <SVGIcon class="c-contributors__avatar-placeholder" name="avatar" />
          </template>
        </a>
      </template>
    </template>
  </div>
</template>

<script lang="ts" setup>
import { ref, onBeforeMount } from 'vue'
import { useRequest, getProject, getProjectContributors } from '@/utils/api'
import SVGIcon from '@/components/SVGIcon.vue'

type Theme = 'banner' | 'card'

const props = withDefaults(defineProps<{
  projectId: number
  theme?: Theme
  limit?: string | undefined
}>(), {
  theme: 'banner',
  limit: undefined
})

const isLoading = ref(true)

const project = ref()

const maxContributors = ref(4)
if (props.limit) {
  maxContributors.value = parseInt(props.limit)
}

const getContributors = useRequest(async () => maxContributors.value ? getProjectContributors(props.projectId, maxContributors.value) : getProjectContributors(props.projectId))

onBeforeMount(async () => {
  project.value = await getProject(props.projectId)

  await getContributors.exec()

  isLoading.value = false
})
</script>
